import { getCrafts } from "@/pages/task-list/repositorie/TaskListRepositorie";
import type { Craft } from "@/shared/work-orders-modals/models/Craft";
import { useQuery } from "@tanstack/react-query";

export type CraftOption = {
	id: number | string;
	description: string;
	color?: string;
	code?: string | null;
};

export const CRAFTS_QUERY_KEY = ["catalogs", "crafts"] as const;

/**
 * The craft list, shared by every screen that filters by craft. Cached under one key so opening
 * Task List and then Work Orders asks for it once, not once per page.
 *
 * The "All crafts" entry is appended here because every filter that uses this list needs it.
 */
export const useCrafts = () => {
	const query = useQuery<CraftOption[], Error>({
		queryKey: CRAFTS_QUERY_KEY,
		queryFn: async () => {
			const res = await getCrafts();
			if (!res.ok) throw new Error(res.message || "Error fetching crafts");

			const rows: Craft[] = res.data?.tables?.[0]?.rows ?? [];
			const options: CraftOption[] = rows.map((row) => ({
				id: row.CraftId,
				description: row.CraftName,
				color: row.Color,
				code: row.Code,
			}));
			options.push({ id: "A", description: "All crafts" });
			return options;
		},
		staleTime: 5 * 60 * 1000,
	});

	return {
		crafts: query.data ?? [],
		isLoading: query.isLoading,
		error: query.error,
	};
};
